import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [animation, setAnimation] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    // Trigger animation after component mounts
    setTimeout(() => setAnimation(true), 100);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simple validation
    if (!loginId || !password) {
      setError('Please enter both login ID and password');
      setIsLoading(false);
      return;
    }

    try {
      await login(loginId, password);
      navigate('/');
    } catch (err: any) {
      // Display the error message from the API
      setError(err.message || 'Invalid username or password');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-between bg-white">
      <div className={`w-full min-h-screen pt-12 pb-6 px-6 flex flex-col transition-all duration-700 transform ${animation ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
        {/* Company Logo */}
        <div className="flex justify-center mb-10 overflow-hidden mt-8">
          <div className={`w-full max-w-[200px] transition-all duration-500 ${logoLoaded ? 'opacity-100' : 'opacity-0'}`}>
            <img 
              src="/logo512.png" 
              alt="Ultra Cleaning Service Logo" 
              className="w-full object-contain"
              onLoad={() => setLogoLoaded(true)}
              onError={(e) => {
                console.error("Error loading logo", e);
                setLogoLoaded(true); // Still mark as loaded to avoid hanging
              }}
            />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">Welcome Back</h1>
        <p className="text-center text-gray-600 mb-10">
          Sign in to your account
        </p>

        <form className="w-full space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="login-id" className="block text-sm font-medium text-gray-700 mb-2">
                Login ID
              </label>
              <input
                id="login-id"
                name="login"
                type="text"
                autoComplete="username"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 text-base"
                placeholder="Enter your login ID"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10 text-base"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-md">
              <p>{error}</p>
            </div>
          )}

          <div className="mt-8">
            <button
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : 'transform active:scale-[0.98]'
              }`}
            >
              {isLoading ? (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </span>
              ) : (
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <svg className="h-5 w-5 text-primary-dark group-hover:text-white transition" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
              <span className="ml-2">{isLoading ? 'Signing in...' : 'Sign in'}</span>
            </button>
          </div>
        </form>

        <div className="mt-auto pt-10 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Ultra Cleaning Service
        </div>
      </div>
    </div>
  );
};

export default Login; 